/**
 * **Pengguna konsol** — siapa saja yang bisa masuk portal ini (Imron, 2026-08-07).
 *
 * Imron: *"role owner pada tenant arena tidak masuk sebagai daftar di staff? Dia bisa reset
 * password semua staff."*
 *
 * ### ⛔ `portal_user` memang bukan `staff_ref`, dan itu benar
 * Akuntan HQ bukan staf outlet mana pun; memaksanya jadi baris staf menaruh orang yang tak pernah
 * masuk outlet ke daftar absensi. Yang salah bukan pemisahannya — yang salah adalah **sisi
 * konsolnya tak punya layar sama sekali**. `bootstrapOwner` menerbitkan satu pengguna lalu menutup
 * pintunya, dan sesudah itu tak ada daftar, tak ada penambahan, tak ada setel ulang. Sandi yang
 * diketik operator sekali saat onboarding jadi sandi selamanya.
 *
 * ### ⚠️ Dua daftar orang, dan bedanya harus TERBACA
 * | Layar | Siapa | Kredensial | Untuk apa |
 * |---|---|---|---|
 * | **ini** | pemilik, manajer area, akuntan | email + kata sandi | mengubah master, membaca laporan |
 * | **Staf** | kasir, pelayan, juru masak | kode staf + **PIN** | bekerja di lantai |
 *
 * Satu orang bisa ada di dua-duanya (pemilik warung yang jadi kasirnya sendiri) — kolom "Staf"
 * menyebutnya bila memang terkait.
 */
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { MIN_PASSWORD_LENGTH } from '@osbpos/contract';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { ConsoleUser } from '../api/client';
import { session } from '../api/session';

const ROLES = ['owner', 'admin', 'manager', 'viewer'] as const;

export function ConsoleUsers() {
  const { t } = useTranslation();
  const saya = session();
  const [rows, setRows] = useState<readonly ConsoleUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [buat, setBuat] = useState(false);
  const [resetFor, setResetFor] = useState<ConsoleUser | null>(null);
  const [sandi, setSandi] = useState('');
  const [form] = Form.useForm<{ email: string; displayName: string; role: string; password: string }>();

  const muat = useCallback(async () => {
    try {
      setRows(await api.consoleUsers());
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => { void muat(); }, [muat]);

  /*
   * ⛔ Layar TIDAK menebak siapa yang boleh: kalau server menolak (`AUTH-403-NOT_OWNER`), pesannya
   * ditampilkan apa adanya. Menyembunyikan tabnya untuk non-pemilik cuma kesopanan; kontrolnya
   * tetap di server, karena layar bisa dilewati.
   */
  const bolehKelola = saya?.role === 'owner' || saya?.role === 'admin';
  /*
   * ⭐⭐ Wakil (`admin`) boleh semuanya KECUALI menyentuh owner (Imron, 2026-08-07). Barisnya
   * dikunci di layar supaya penolakan server tak perlu terjadi — tapi ⛔ kontrolnya tetap di
   * server (`AUTH-403-OWNER_PROTECTED`), karena layar bisa dilewati.
   */
  const bolehSentuh = (r: ConsoleUser) => saya?.role === 'owner' || r.role !== 'owner';

  const columns = [
    { title: t('consoleUsers.email'), dataIndex: 'email' },
    { title: t('consoleUsers.name'), dataIndex: 'displayName' },
    {
      title: t('consoleUsers.role'),
      dataIndex: 'role',
      width: 180,
      render: (v: string, r: ConsoleUser) =>
        bolehKelola && bolehSentuh(r) ? (
          <Select
            size="small"
            style={{ width: 130 }}
            value={v}
            data-testid={`peran-${r.email}`}
            /* ⛔ `owner` tak ikut ditawarkan kepada wakil: mengangkat pemilik baru adalah jalan
               paling halus menuju "wakil jadi setara", dan ia tak menyentuh baris owner mana pun. */
            options={ROLES.filter((x) => x !== 'owner' || saya?.role === 'owner')
              .map((x) => ({ value: x, label: t(`consoleUsers.roleName.${x}`) }))}
            onChange={(next) => {
              void api.setConsoleUserRole(r.id, next)
                .then(() => muat())
                .catch((e: unknown) => {
                  void message.error(e instanceof Error ? e.message : String(e));
                });
            }}
          />
        ) : <Tag>{v}</Tag>,
    },
    {
      title: t('consoleUsers.staff'),
      dataIndex: 'staffCode',
      width: 120,
      /* ⚠️ Kosong itu NORMAL — akuntan HQ memang bukan staf outlet mana pun. */
      render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: t('consoleUsers.lastLogin'),
      dataIndex: 'lastLoginAt',
      width: 150,
      render: (v: string | null) =>
        v === null
          ? <Tag data-testid="belum-pernah">{t('consoleUsers.never')}</Tag>
          : v.slice(0, 16).replace('T', ' '),
    },
    {
      title: t('consoleUsers.status'),
      width: 150,
      render: (_: unknown, r: ConsoleUser) => (
        <Space size={4}>
          <Tag color={r.isActive ? 'green' : 'default'}>
            {r.isActive ? t('consoleUsers.active') : t('consoleUsers.inactive')}
          </Tag>
          {/* ⭐ Terkunci DISEBUT: tanpa ini, "sudah saya reset kok masih tak bisa masuk" tak
              punya jawaban yang terlihat di layar mana pun. */}
          {r.locked && <Tag color="red" data-testid={`terkunci-${r.email}`}>{t('consoleUsers.locked')}</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      width: 210,
      render: (_: unknown, r: ConsoleUser) =>
        bolehKelola && bolehSentuh(r) ? (
          <Space size={4}>
            <Button size="small" data-testid={`reset-${r.email}`}
                    onClick={() => { setResetFor(r); setSandi(''); }}>
              {t('consoleUsers.reset')}
            </Button>
            <Button
              size="small"
              danger={r.isActive}
              data-testid={`status-${r.email}`}
              onClick={() => {
                void api.setConsoleUserStatus(r.id, !r.isActive)
                  .then(() => muat())
                  .catch((e: unknown) => {
                    void message.error(e instanceof Error ? e.message : String(e));
                  });
              }}
            >
              {r.isActive ? t('consoleUsers.deactivate') : t('consoleUsers.activate')}
            </Button>
          </Space>
        ) : bolehKelola ? (
          /* ⚠️ Menyebut SEBABNYA kosong — baris tanpa tombol tanpa penjelasan terbaca sebagai bug. */
          <Typography.Text type="secondary" data-testid={`terkunci-owner-${r.email}`}>
            {t('consoleUsers.ownerProtected')}
          </Typography.Text>
        ) : null,
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="console-users">
      {/*
        ⚠️ Menyebut BEDANYA dengan layar Staf di depan. Tanpa ini, pemilik yang mencari kasirnya di
        sini akan menyimpulkan datanya hilang — dan yang mencari akuntannya di layar Staf juga.
      */}
      <Alert type="info" showIcon data-testid="beda-staf" title={t('consoleUsers.vsStaff')} />

      {error !== null && <Alert type="error" showIcon data-testid="cu-error" title={error} />}

      {bolehKelola && (
        <Card size="small">
          <Button type="primary" data-testid="tambah-pengguna" onClick={() => { setBuat(true); }}>
            {t('consoleUsers.add')}
          </Button>
        </Card>
      )}

      <Table<ConsoleUser>
        size="small"
        rowKey="id"
        dataSource={rows as ConsoleUser[]}
        columns={columns}
        pagination={false}
        data-testid="tabel-pengguna"
      />

      <Modal
        open={buat}
        title={t('consoleUsers.add')}
        okText={t('consoleUsers.create')}
        data-testid="dialog-buat"
        onCancel={() => { setBuat(false); form.resetFields(); }}
        onOk={() => {
          void form.validateFields().then(async (v) => {
            try {
              await api.createConsoleUser(v);
              void message.success(t('consoleUsers.created', { email: v.email }));
              setBuat(false);
              form.resetFields();
              await muat();
            } catch (e: unknown) {
              void message.error(e instanceof Error ? e.message : String(e));
            }
          });
        }}
      >
        <Form form={form} layout="vertical" initialValues={{ role: 'viewer' }}>
          <Form.Item name="email" label={t('consoleUsers.email')} rules={[{ required: true, type: 'email' }]}>
            <Input data-testid="isi-email" autoComplete="off" />
          </Form.Item>
          <Form.Item name="displayName" label={t('consoleUsers.name')} rules={[{ required: true }]}>
            <Input data-testid="isi-nama" />
          </Form.Item>
          <Form.Item name="role" label={t('consoleUsers.role')}>
            <Select
              data-testid="isi-peran"
              options={ROLES.filter((x) => x !== 'owner' || saya?.role === 'owner')
                .map((x) => ({ value: x, label: t(`consoleUsers.roleName.${x}`) }))}
            />
          </Form.Item>
          {/* ⚠️ Panjang minimumnya SAMA dengan yang ditegakkan server; layar cuma mengatakannya
              lebih awal supaya penolakan tak terjadi sesudah formulir diisi penuh. */}
          <Form.Item
            name="password"
            label={t('consoleUsers.password')}
            rules={[{ required: true, min: MIN_PASSWORD_LENGTH,
                     message: t('consoleUsers.weak', { n: MIN_PASSWORD_LENGTH }) }]}
          >
            <Input.Password data-testid="isi-sandi" autoComplete="new-password" />
          </Form.Item>
          <Typography.Text type="secondary">{t('consoleUsers.roleHint')}</Typography.Text>
        </Form>
      </Modal>

      <Modal
        open={resetFor !== null}
        title={t('consoleUsers.resetFor', { email: resetFor?.email ?? '' })}
        okText={t('consoleUsers.reset')}
        data-testid="dialog-reset"
        onCancel={() => { setResetFor(null); }}
        onOk={() => {
          if (resetFor === null) return;
          void api.resetConsoleUserPassword(resetFor.id, sandi)
            .then((r) => {
              /* ⭐ Menyebut bahwa kuncinya IKUT dibuka — itu setengah dari gunanya. */
              void message.success(
                r.unlocked ? t('consoleUsers.resetUnlocked') : t('consoleUsers.resetOk'),
              );
              setResetFor(null);
              return muat();
            })
            .catch((e: unknown) => {
              void message.error(e instanceof Error ? e.message : String(e));
            });
        }}
      >
        <Typography.Paragraph type="secondary">{t('consoleUsers.resetHint')}</Typography.Paragraph>
        <Input.Password
          data-testid="reset-sandi"
          value={sandi}
          autoComplete="new-password"
          onChange={(e) => { setSandi(e.target.value); }}
        />
      </Modal>
    </Space>
  );
}
